import type { OrganizationScope } from "@/types/organization-scope"

type OrganizationScopeTreeMeta = {
  treeDepth: 0 | 1
  treeRole: "chain-root" | "chain-child" | "shop"
  treeParentId?: string
  treeChildrenCount: number
}

export type OrganizationScopeTreeNode<T extends OrganizationScope = OrganizationScope> = T & OrganizationScopeTreeMeta

export type OrganizationScopeTreeGroup<T extends OrganizationScope = OrganizationScope> = {
  key: string
  title: string
  desc: string
  items: Array<OrganizationScopeTreeNode<T>>
}

function normalizeText(value?: string | number | null) {
  return String(value ?? "").trim()
}

function getBrandKey(scope: OrganizationScope) {
  const brandId = normalizeText(scope.meta?.brandId)
  return brandId ? `brand-${brandId}` : ""
}

function getBrandTitle(scope?: OrganizationScope) {
  const name = normalizeText(scope?.name)
  if (!name) return "连锁机构"
  return name
    .replace(/\s*·\s*总部$/, "")
    .replace(/\s*总部$/, "")
    .trim() || name
}

function getChainStoreBrandTitle(scope?: OrganizationScope) {
  const name = normalizeText(scope?.name)
  const [brandName] = name.split("·").map(part => part.trim()).filter(Boolean)
  return brandName || "连锁机构"
}

function toTreeNode<T extends OrganizationScope>(
  scope: T,
  tree: OrganizationScopeTreeMeta,
): OrganizationScopeTreeNode<T> {
  return {
    ...scope,
    ...tree,
  }
}

export function buildOrganizationScopeTree<T extends OrganizationScope>(
  scopes: T[],
): Array<OrganizationScopeTreeGroup<T>> {
  const roots = new Map<string, T>()
  const children = new Map<string, T[]>()
  const chainOrder: string[] = []
  const shops: T[] = []

  const ensureChainKey = (key: string) => {
    if (!chainOrder.includes(key)) chainOrder.push(key)
  }

  scopes.forEach((scope) => {
    if (scope.type === "chain-headquarter") {
      const key = getBrandKey(scope) || `scope-${scope.id}`
      roots.set(key, scope)
      ensureChainKey(key)
      return
    }
    if (scope.type === "chain-store") {
      const key = getBrandKey(scope) || `scope-${scope.id}`
      children.set(key, [...(children.get(key) || []), scope])
      ensureChainKey(key)
      return
    }
    shops.push(scope)
  })

  const chainGroups = chainOrder.map((key) => {
    const root = roots.get(key)
    const branchStores = children.get(key) || []
    const rootItems = root
      ? [toTreeNode(root, {
          treeDepth: 0,
          treeRole: "chain-root",
          treeChildrenCount: branchStores.length,
        })]
      : []
    const branchItems = branchStores.map(store => toTreeNode(store, {
      treeDepth: root ? 1 : 0,
      treeRole: root ? "chain-child" : "shop",
      treeParentId: root?.id,
      treeChildrenCount: 0,
    }))
    return {
      key,
      title: root ? getBrandTitle(root) : getChainStoreBrandTitle(branchStores[0]),
      desc: branchStores.length > 0 ? `${branchStores.length} 家分店` : "总部视图",
      items: [...rootItems, ...branchItems],
    }
  })

  const shopGroups: Array<OrganizationScopeTreeGroup<T>> = shops.length > 0
    ? [{
        key: "independent-shops",
        title: "独立门店",
        desc: `${shops.length} 家门店`,
        items: shops.map(shop => toTreeNode(shop, {
          treeDepth: 0,
          treeRole: "shop",
          treeChildrenCount: 0,
        })),
      }]
    : []

  return [...chainGroups, ...shopGroups]
}
